import { z } from 'zod';

/**
 * MCP access tokens (issue #31, `docs/specs/mcp.md` D3, D4, D21): the contract
 * between the token API and the « Accès MCP » section of Réglages.
 */

// ── Constants ────────────────────────────────────────────────────────────────

/** Every personal token value starts with this prefix. */
export const MCP_TOKEN_PREFIX = 'mss_pat_';
/** A `pms_admin` holds at most this many active tokens per environment. */
export const MCP_MAX_ACTIVE_TOKENS = 5;
export const MCP_TOKEN_NAME_MAX_LENGTH = 80;
/** The request header carrying the token (the Authorization header is Authentik's on dev). */
export const MCP_TOKEN_HEADER = 'X-Mss-Mcp-Token';

// ── Error codes ──────────────────────────────────────────────────────────────

/** 409 on token creation: the account already holds `MCP_MAX_ACTIVE_TOKENS` active tokens. */
export const MCP_TOKEN_LIMIT_REACHED = 'MCP_TOKEN_LIMIT_REACHED';
/** 403 on any write attempted through the MCP of prod. */
export const MCP_READ_ONLY = 'MCP_READ_ONLY';

// ── Environment ──────────────────────────────────────────────────────────────

/** Which deployment answers: prod is read-only, dev reads and writes. */
export const mcpEnvironmentSchema = z.enum(['dev', 'prod']);
export type McpEnvironment = z.infer<typeof mcpEnvironmentSchema>;

// ── Responses ────────────────────────────────────────────────────────────────

export const mcpTokenStatusSchema = z.enum(['active', 'revoked']);
export type McpTokenStatus = z.infer<typeof mcpTokenStatusSchema>;

/** A token as the API shows it. Never carries the value or its hash. */
export const mcpTokenSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  createdAt: z.string().datetime(),
  lastUsedAt: z.string().datetime().nullable(),
  revokedAt: z.string().datetime().nullable(),
  status: mcpTokenStatusSchema,
});
export type McpToken = z.infer<typeof mcpTokenSchema>;

/** The caller's tokens on this environment, with the environment and the active-token ceiling. */
export const mcpTokenListSchema = z.object({
  environment: mcpEnvironmentSchema,
  maxActive: z.number().int().positive(),
  tokens: z.array(mcpTokenSchema),
});
export type McpTokenList = z.infer<typeof mcpTokenListSchema>;

/** The answer to a creation: `value` is the plaintext and is shown once, never stored. */
export const createdMcpTokenSchema = z.object({
  token: mcpTokenSchema,
  value: z.string().startsWith(MCP_TOKEN_PREFIX),
});
export type CreatedMcpToken = z.infer<typeof createdMcpTokenSchema>;

// ── Requests ─────────────────────────────────────────────────────────────────

/** `POST` body of a token creation. */
export const createMcpTokenBodySchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Donnez un nom au jeton.')
    .max(
      MCP_TOKEN_NAME_MAX_LENGTH,
      `Le nom tient en ${MCP_TOKEN_NAME_MAX_LENGTH} caractères au plus.`,
    ),
});
export type CreateMcpTokenBody = z.infer<typeof createMcpTokenBodySchema>;
